import type { FC, KeyboardEvent, PointerEvent } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ChevronsLeftRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { clamp } from '@/lib/frame'
import { useInView } from '@/hooks/use-in-view'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'

type Tile =
    | { kind: 'photo', src: string, position?: string, img?: string }
    | { kind: 'type', text: string, className: string, textClassName?: string }

type Profile = {
    handle: string
    name: string
    stats: Array<[string, string]>
    bio: Array<string>
    tiles: Array<Tile>
}

const before: Profile = {
    handle: '@hearth_hollow_official_store',
    name: 'HEARTH & HOLLOW CERAMICS!!',
    stats: [['187', 'posts'], ['1,093', 'followers'], ['4,212', 'following']],
    bio: ['mugs, candles, bowls & more!!!', 'prices in dms... link in bio', 'new stuff every day??'],
    tiles: [
        { kind: 'photo', src: '/images/social/creator-3.jpg', position: '70% 40%', img: '[filter:saturate(1.6)_hue-rotate(-24deg)]' },
        { kind: 'type', text: 'MEGA CLEARANCE 25% OFF!!!', className: 'bg-[#e3262e]', textClassName: 'font-serif text-[#ffe500] italic font-black uppercase -rotate-6 text-center' },
        { kind: 'photo', src: '/images/work/fitness-2.jpg', position: '50% 35%', img: '[filter:contrast(1.25)_saturate(1.5)]' },
        { kind: 'type', text: 'Monday vibes!!', className: 'bg-gradient-to-br from-[#7b2ff7] to-[#f107a3]', textClassName: 'font-serif text-white text-center italic' },
        { kind: 'photo', src: '/images/work/tech-2.jpg', position: '50% 55%' },
        { kind: 'type', text: 'like 4 like', className: 'bg-[#b6f23c]', textClassName: 'font-mono uppercase text-black text-center' },
        { kind: 'photo', src: '/images/social/studio-3.jpg', position: '50% 40%', img: '[filter:saturate(1.7)]' },
        { kind: 'photo', src: '/images/work/lifestyle-2.jpg', position: '55% 60%', img: '[filter:saturate(2.2)_hue-rotate(-28deg)_brightness(1.12)]' },
        { kind: 'type', text: 'shop now link in bio', className: 'bg-[#1d4ed8]', textClassName: 'font-serif text-white underline text-center' }
    ]
}

const after: Profile = {
    handle: '@hearthandhollow',
    name: 'Hearth & Hollow',
    stats: [['226', 'posts'], ['27.4K', 'followers'], ['288', 'following']],
    bio: ['Hand-thrown in the Lake District.', 'Forecast: mostly clay.', 'Sunday broadcasts below.'],
    tiles: [
        { kind: 'photo', src: '/images/work/lifestyle-1.jpg', position: '50% 55%' },
        { kind: 'type', text: 'Mostly clay, some weather.', className: 'bg-[#e7d3bf]', textClassName: 'font-heading font-bold tracking-[-0.04em] text-ink' },
        { kind: 'photo', src: '/images/work/lifestyle-2.jpg', position: '55% 62%' },
        { kind: 'type', text: 'Moss glaze. Friday, 6pm.', className: 'bg-[#b07a5a]', textClassName: 'font-heading font-bold tracking-[-0.04em] text-cream' },
        { kind: 'photo', src: '/images/work/lifestyle-3.jpg', position: '42% 50%' },
        { kind: 'photo', src: '/images/work/lifestyle-1.jpg', position: '18% 82%', img: 'scale-[1.7]' },
        { kind: 'type', text: 'Sunday broadcast, no. 22', className: 'bg-ink', textClassName: 'font-heading font-bold tracking-[-0.04em] text-cream' },
        { kind: 'photo', src: '/images/work/lifestyle-2.jpg', position: '18% 22%', img: 'scale-[1.5]' },
        { kind: 'type', text: 'Your mugs, in your kitchens.', className: 'bg-cream ring-1 ring-inset ring-ink/10', textClassName: 'font-heading font-bold tracking-[-0.04em] text-ink' }
    ]
}

const ProfileMock: FC<{ profile: Profile, variant: 'before' | 'after' }> = ({ profile, variant }) => {
    const isAfter = variant === 'after'

    return (
        <div className={cn(
            'flex flex-col gap-4 p-4 sm:p-6 lg:absolute lg:inset-0 lg:flex-row lg:gap-10 lg:p-10',
            isAfter ? 'absolute inset-0 bg-paper' : 'relative bg-[#f3f3f5]'
        )}>
            <div className='flex flex-col lg:w-[36%] lg:py-2'>
                <div className='flex items-center gap-4'>
                    {isAfter ? (
                        <span className='flex size-14 shrink-0 items-center justify-center rounded-full bg-[#b07a5a] font-heading text-lg font-bold tracking-[-0.035em] text-cream ring-2 ring-paper ring-offset-2 ring-offset-[#b07a5a] lg:size-20 lg:text-2xl'>
                            h&amp;h
                        </span>
                    ) : (
                        <Photo
                            src='/images/work/fitness-2.jpg'
                            alt=''
                            sizes='80px'
                            position='50% 20%'
                            tone={false}
                            className='size-14 shrink-0 rounded-full lg:size-20'
                            imgClassName='[filter:blur(1px)_saturate(1.4)]'
                        />
                    )}

                    <div className='min-w-0'>
                        <p className={cn('truncate text-sm font-semibold lg:text-base', isAfter ? 'text-ink' : 'font-serif text-[#222]')}>
                            {profile.handle}
                        </p>
                        <p className={cn('truncate text-xs lg:text-sm', isAfter ? 'text-stone' : 'text-[#555]')}>
                            {profile.name}
                        </p>
                    </div>
                </div>

                <div className='mt-4 grid grid-cols-3 gap-2 lg:mt-6'>
                    {profile.stats.map(([value, label]) => (
                        <div key={label}>
                            <p className={cn('text-base font-bold lg:text-2xl', isAfter ? 'font-heading tracking-[-0.04em]' : 'font-serif')}>
                                {value}
                            </p>
                            <p className='text-[10px] text-stone lg:text-xs'>{label}</p>
                        </div>
                    ))}
                </div>

                <div className='mt-3 hidden text-xs leading-relaxed sm:block lg:mt-5 lg:text-sm'>
                    {profile.bio.map(line => (
                        <p
                            key={line}
                            className={isAfter ? 'text-ink/80' : 'font-serif text-[#333]'}
                        >
                            {line}
                        </p>
                    ))}
                </div>

                <div className='mt-auto hidden gap-2 pt-5 lg:flex'>
                    <span className={cn('flex-1 rounded-lg py-2 text-center text-sm font-semibold', isAfter ? 'bg-ink text-cream' : 'bg-[#3897f0] text-white')}>
                        Follow
                    </span>
                    <span className={cn('flex-1 rounded-lg py-2 text-center text-sm font-semibold', isAfter ? 'bg-sand text-ink' : 'bg-[#e4e4e7] text-[#222]')}>
                        Message
                    </span>
                </div>
            </div>

            <div className='grid grid-cols-3 gap-1 lg:aspect-square lg:h-full lg:flex-none lg:grid-rows-3 lg:gap-1.5'>
                {profile.tiles.map((tile, index) => (
                    <div
                        key={index}
                        className='relative aspect-square overflow-hidden lg:aspect-auto'
                    >
                        {tile.kind === 'photo' ? (
                            <Photo
                                src={tile.src}
                                alt=''
                                sizes='(min-width: 1024px) 240px, 33vw'
                                position={tile.position}
                                tone={isAfter}
                                className='absolute inset-0'
                                imgClassName={tile.img}
                            />
                        ) : (
                            <div className={cn('flex h-full items-center justify-center p-2 lg:items-end lg:justify-start lg:p-4', tile.className)}>
                                <span className={cn('text-[10px] leading-tight sm:text-sm lg:text-lg lg:leading-[1.02]', tile.textClassName)}>
                                    {tile.text}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export const BeforeAfter: FC = () => {
    const [position, setPosition] = useState(50)
    const [hinting, setHinting] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const dragging = useRef(false)
    const touched = useRef(false)
    const timers = useRef<Array<number>>([])
    const { ref: viewRef, inView } = useInView<HTMLDivElement>({ rootMargin: '0px 0px -30% 0px' })

    const stopHint = () => {
        touched.current = true
        timers.current.forEach(timer => window.clearTimeout(timer))
        timers.current = []
        setHinting(false)
    }

    useEffect(() => {
        if (!inView || touched.current) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const steps: Array<[number, number]> = [[0, 66], [800, 36], [1600, 50]]

        setHinting(true)
        steps.forEach(([wait, value]) => {
            timers.current.push(window.setTimeout(() => setPosition(value), wait))
        })
        timers.current.push(window.setTimeout(() => setHinting(false), 2400))

        const list = timers.current

        return () => list.forEach(timer => window.clearTimeout(timer))
    }, [inView])

    const moveTo = (clientX: number) => {
        const rect = containerRef.current?.getBoundingClientRect()

        if (!rect) return

        setPosition(clamp(((clientX - rect.left) / rect.width) * 100, 0, 100))
    }

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        stopHint()
        dragging.current = true
        event.currentTarget.setPointerCapture(event.pointerId)
        moveTo(event.clientX)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (dragging.current) moveTo(event.clientX)
    }

    const onPointerUp = () => {
        dragging.current = false
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const steps: Record<string, number> = {
            ArrowLeft: -4,
            ArrowDown: -4,
            ArrowRight: 4,
            ArrowUp: 4,
            PageDown: -15,
            PageUp: 15
        }

        if (event.key in steps) {
            event.preventDefault()
            stopHint()
            setPosition(value => clamp(value + steps[event.key], 0, 100))
        }

        if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault()
            stopHint()
            setPosition(event.key === 'Home' ? 0 : 100)
        }
    }

    return (
        <section
            id='before-after'
            aria-labelledby='before-after-title'
            className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
        >
            <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                <div>
                    <SectionLabel index='09'>Before and after</SectionLabel>
                    <SplitText
                        as='h2'
                        id='before-after-title'
                        lines={['Same mugs.', 'Whole new forecast.']}
                        italic='forecast'
                        className='mt-5 text-[clamp(2.75rem,6.4vw,6rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                    />
                </div>

                <Reveal
                    as='p'
                    delay={200}
                    className='max-w-sm text-lg leading-relaxed text-stone'
                >
                    Drag the handle. On the left, a lovely pottery studio shouting into the void. On the right, the same studio eight months later, with a voice, a rhythm and a waiting list.
                </Reveal>
            </div>

            <Reveal
                delay={150}
                className='mt-12'
            >
                <div ref={viewRef}>
                    <div
                        ref={containerRef}
                        data-cursor='Drag'
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        className='relative w-full touch-pan-y select-none overflow-hidden rounded-[1.75rem] border border-black/5 shadow-[0_40px_80px_-50px_rgb(30_30_30/0.55)] md:rounded-[2.25rem] lg:aspect-[16/9]'
                    >
                        <div aria-hidden='true'>
                            <ProfileMock
                                profile={before}
                                variant='before'
                            />
                        </div>

                        <div
                            aria-hidden='true'
                            className={cn('absolute inset-0', hinting && 'transition-[clip-path] duration-700 ease-expo')}
                            style={{ clipPath: `inset(0 0 0 ${position}%)` }}
                        >
                            <ProfileMock
                                profile={after}
                                variant='after'
                            />
                        </div>

                        <span className='pointer-events-none absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white md:left-6 md:top-6'>
                            Before
                        </span>
                        <span className='pointer-events-none absolute right-4 top-4 rounded-full bg-ember px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ink md:right-6 md:top-6'>
                            After
                        </span>

                        <div
                            className={cn('pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgb(0_0_0/0.08)]', hinting && 'transition-[left] duration-700 ease-expo')}
                            style={{ left: `${position}%` }}
                        >
                            <div
                                role='slider'
                                tabIndex={0}
                                aria-label='Compare the feed before and after'
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={Math.round(position)}
                                aria-valuetext={`${Math.round(100 - position)}% of the new feed visible`}
                                onKeyDown={onKeyDown}
                                className='pointer-events-auto absolute left-1/2 top-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ember text-ink shadow-[0_12px_30px_-10px_rgb(255_92_0/0.8)] transition-transform hover:scale-105'
                            >
                                <ChevronsLeftRight className='size-6' />
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>

            <Reveal
                delay={200}
                className='mt-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between'
            >
                <dl className='grid grid-cols-3 gap-6 md:gap-12'>
                    {[['+71%', 'sales from social'], ['25x', 'the followers'], ['14', 'sold-out launches']].map(([value, label]) => (
                        <div key={label}>
                            <dt className='sr-only'>{label}</dt>
                            <dd className='font-heading text-3xl font-bold tracking-[-0.035em] md:text-4xl'>{value}</dd>
                            <dd className='mt-1 font-mono text-[11px] text-stone'>{label}</dd>
                        </div>
                    ))}
                </dl>

                <PageLink
                    href='/work/hearth-and-hollow'
                    className='group inline-flex items-center gap-2 text-base font-medium underline decoration-ember decoration-2 underline-offset-[6px]'
                >
                    Read the Hearth & Hollow case notes
                    <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                </PageLink>
            </Reveal>
        </section>
    )
}
